import { Dialog, Empty, Button, lucideIcons, Card, toast } from '@round/creatorbase-ui';
import styles from './AddCreatorModal.module.css';
import { FormikProvider, useFormik } from 'formik';
import { creatorbase } from '@round/api';
import Form from './Form/Form';
import { FormState, defaultFormValues, validationSchema } from './helpers';
import { platformIcons, platformLabels } from 'Modules/Plans/helpers';
import PlatformSelect from './PlatformSelect';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';

const { XIcon } = lucideIcons;

const flattenValidationErrors = (errors: unknown): string[] => {
    if (Array.isArray(errors)) {
        return errors;
    }

    if (typeof errors === 'string') {
        return [errors];
    }

    if (errors && typeof errors === 'object') {
        return Object.values(errors).flatMap(flattenValidationErrors);
    }

    return [];
};

type Props = {
    isOpen: boolean;
    onClose: () => void;
    onCreated?: () => void;
};

const AddCreatorModal = ({ isOpen, onClose, onCreated }: Props) => {
    const { selectedClientId } = useClientSelect();

    const formik = useFormik<FormState>({
        initialValues: {
            activePlatforms: [],
            location: null,
            contact_details: '',
            paypal_email: '',
            content_tags: [],
            notes: '',
            instagram: defaultFormValues,
            tiktok: defaultFormValues,
            youtube: defaultFormValues,
        },
        validationSchema,
        validateOnMount: true,
        onSubmit: async (values, { resetForm }) => {
            if (!selectedClientId) {
                return;
            }

            const payload = values.activePlatforms.reduce(
                (acc, platform) => {
                    // youtube is not supported yet
                    acc[platform as 'tiktok' | 'instagram'] = {
                        identifier: values[platform].profileUrl?.trim() || '',
                        currency_id: values[platform].cost?.currency?.value || null,
                        cost: Number(values[platform].cost?.value),
                    };

                    return acc;
                },
                {
                    client_id: selectedClientId,
                    content_tags: values.content_tags?.map((tag) => tag.value) || [],
                    location: values.location?.value || null,
                    paypal_email: values.paypal_email?.trim() || null,
                    notes: values.notes.trim(),
                    contact_details: values.contact_details.trim(),
                    tiktok: null,
                    instagram: null,
                } as creatorbase.PostClientInfluencerAccountData
            );

            try {
                const response = await creatorbase.postClientInfluencerAccount(payload);
                if (response.status === 400) {
                    const messages = flattenValidationErrors(response.data);
                    toast.error(messages.length ? messages.join('\n') : 'Could not create creator');
                    return;
                }
                if (response.status !== 201) {
                    toast.error('Could not create creator');
                    return;
                }

                toast.info('Creator created successfully');
                onCreated?.();
                onClose();
                resetForm();
            } catch {
                toast.error('Could not create creator');
            }
        },
    });

    const addPlatform = (platform: creatorbase.Platform) => {
        formik.setFieldValue('activePlatforms', [...formik.values.activePlatforms, platform]);
        formik.validateForm();
    };

    const removePlatform = (platform: creatorbase.Platform) => {
        const resultingPlatforms = formik.values.activePlatforms.filter((p) => p !== platform);
        formik.setFieldValue('activePlatforms', resultingPlatforms);
        if (!resultingPlatforms.length) {
            formik.validateForm();
        }
    };

    const activePlatforms = formik.values.activePlatforms;
    const [firstPlatform, ...restActivePlatforms] = formik.values.activePlatforms;

    const handleClose = () => {
        formik.resetForm();
        onClose();
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <Dialog.Content className={styles.modalContent}>
                <FormikProvider value={formik}>
                    <Dialog.Header>
                        <Dialog.Title>Add creator</Dialog.Title>
                        <Dialog.Description size="xs">
                            Enter creator details and add their social platform information below.
                        </Dialog.Description>
                    </Dialog.Header>

                    <Dialog.Body>
                        {!activePlatforms.length ? (
                            <Empty>
                                <Empty.Header>
                                    <Empty.Description>Add a platform to get started</Empty.Description>
                                </Empty.Header>
                                <Empty.Content className={styles.emptyContent}>
                                    <PlatformSelect activePlatforms={activePlatforms} onPlatformSelect={addPlatform} />
                                </Empty.Content>
                            </Empty>
                        ) : (
                            <div className={styles.scrollArea}>
                                <PlatformSelect activePlatforms={activePlatforms} onPlatformSelect={addPlatform} />
                                <div className={styles.forms}>
                                    {/* First form should be full height and the rest stacked on top of each other */}
                                    <FormCard platform={firstPlatform} removePlatform={removePlatform} />
                                    <div className={styles.restActiveForms}>
                                        {restActivePlatforms.map((platform) => (
                                            <FormCard
                                                key={platform}
                                                platform={platform}
                                                removePlatform={removePlatform}
                                            />
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                    </Dialog.Body>
                </FormikProvider>

                <Dialog.Footer>
                    <Button size="sm" variant="outline" disabled={formik.isSubmitting} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        variant="default"
                        onClick={formik.submitForm}
                        disabled={!formik.isValid || formik.isSubmitting}
                        isLoading={formik.isSubmitting}
                    >
                        Add creator
                    </Button>
                </Dialog.Footer>
            </Dialog.Content>
        </Dialog>
    );
};

type FormCardProps = { platform: creatorbase.Platform; removePlatform: (platform: creatorbase.Platform) => void };

function FormCard({ platform, removePlatform }: FormCardProps) {
    const Icon = platformIcons[platform];
    return (
        <Card key={platform} className={styles.form}>
            <Card.Header>
                <Card.Title className={styles.formTitle}>
                    <Icon width="16" height="16" />
                    {platformLabels[platform]}
                </Card.Title>

                <Card.Action>
                    <Button variant="bare" size="icon-xs" onClick={() => removePlatform(platform)}>
                        <XIcon size="14" />
                    </Button>
                </Card.Action>
            </Card.Header>

            <Card.Content>
                <Form platform={platform} />
            </Card.Content>
        </Card>
    );
}

export default AddCreatorModal;
